import React, { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import TaskStageBadge from "../components/TaskStageBadge";
import { isTaskDone } from "../lib/taskStage";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  DollarSign,
  CheckCircle2,
  Clock,
  Briefcase,
  TrendingUp,
  FileText,
  Layers,
  Receipt,
} from "lucide-react";
import { DeadlinePhasesBadge } from "../components/DeadlinePhases";

const ArtistDashboardPage = () => {
  const { user } = useAuth();
  const { displayCurrency, getCurrencySymbol, rates } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);

  const usdToUah = rates?.USD || 41.5;
  const eurToUah = rates?.EUR || 43.5;

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      const [paymentsRes, tasksRes, projectsRes] = await Promise.all([
        api.get("/artist/my-payments").catch(() => ({ data: [] })),
        api.get("/tasks").catch(() => ({ data: [] })),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      
      setPayments(paymentsRes.data || []);
      setTasks(tasksRes.data || []);
      setProjects(projectsRes.data || []);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const toDisplayCurrency = (amount, currency = "UAH") => {
    if (!amount) return 0;
    let amountUAH = amount;
    if (currency === "USD") amountUAH = amount * usdToUah;
    else if (currency === "EUR") amountUAH = amount * eurToUah;
    
    if (displayCurrency === "USD") return amountUAH / usdToUah;
    if (displayCurrency === "EUR") return amountUAH / eurToUah;
    return amountUAH;
  };

  const formatAmount = (amount, currency = "UAH") => {
    const converted = toDisplayCurrency(amount, currency);
    const symbol = getCurrencySymbol();
    return `${symbol}${converted.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Separate real payments from task earnings
  const { realPayments, taskEarnings } = useMemo(() => {
    const real = [];
    const earnings = [];
    payments.forEach(p => {
      if (p.type === "task_earning") {
        earnings.push(p);
      } else {
        real.push(p);
      }
    });
    return { realPayments: real, taskEarnings: earnings };
  }, [payments]);

  // Stats — separated Task Amount vs To Pay vs Paid
  const stats = useMemo(() => {
    // Task Amount = sum of all task agreed_prices (task_earning entries)
    let taskAmount = 0;
    taskEarnings.forEach(p => {
      taskAmount += toDisplayCurrency(p.amount, p.currency);
    });
    
    // To Pay = real invoiced amounts that are NOT paid
    let toPayAmount = 0;
    // Paid = real payments that ARE paid
    let paidAmount = 0;
    
    realPayments.forEach(p => {
      const amountConverted = toDisplayCurrency(p.amount, p.currency);
      if (p.status === "paid") {
        paidAmount += amountConverted;
      } else {
        toPayAmount += amountConverted;
      }
    });
    
    return {
      taskAmount,
      toPayAmount,
      paidAmount,
      totalInvoiced: toPayAmount + paidAmount,
      realPaymentsCount: realPayments.length,
      taskEarningsCount: taskEarnings.length,
      activeTasks: tasks.filter(t => !isTaskDone(t)).length,
      completedTasks: tasks.filter(t => isTaskDone(t)).length,
      activeProjects: projects.length,
    };
  }, [realPayments, taskEarnings, tasks, projects, displayCurrency, usdToUah, eurToUah]);

  // Monthly chart — ONLY real payments (not task_earnings)
  const monthlyData = useMemo(() => {
    const months = {};
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    
    for (let i = 0; i < 12; i++) {
      const key = `2026-${String(i + 1).padStart(2, "0")}`;
      months[key] = { name: monthNames[i], paid: 0, pending: 0 };
    }
    
    realPayments.forEach(p => {
      const dateStr = p.date || p.created_at?.substring(0, 10);
      const month = dateStr?.substring(0, 7) || "2026-01";
      if (months[month]) {
        const amountConverted = toDisplayCurrency(p.amount, p.currency);
        if (p.status === "paid") {
          months[month].paid += amountConverted;
        } else {
          months[month].pending += amountConverted;
        }
      }
    });
    
    return Object.values(months);
  }, [realPayments, displayCurrency, usdToUah, eurToUah]);

  // Pie chart — real payments only
  const statusData = useMemo(() => {
    const paid = realPayments.filter(p => p.status === "paid").length;
    const pending = realPayments.filter(p => p.status !== "paid").length;
    return [
      { name: "Paid", value: paid, color: "#22c55e" },
      { name: "To Pay", value: pending, color: "#f59e0b" },
    ].filter(d => d.value > 0);
  }, [realPayments]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  const symbol = getCurrencySymbol();

  const typeLabel = (type) => {
    if (type === "task_earning") return "Task";
    if (type === "task_payment") return "Invoice";
    if (type === "artist_payment") return "Payment";
    return type;
  };

  const typeBadgeClass = (type) => {
    if (type === "task_earning") return "bg-zinc-800 text-zinc-400 border-zinc-700";
    if (type === "task_payment") return "bg-orange-900/30 text-orange-400 border-orange-700";
    return "bg-blue-900/30 text-blue-400 border-blue-700";
  };

  return (
    <div data-testid="artist-dashboard-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope'] flex items-center gap-3">
          <Layers className="h-7 w-7 text-pink-500" />
          My Dashboard
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Welcome back, {user?.name}! Here's your work overview.
        </p>
      </div>

      {/* Stats Cards — 5 cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {/* Task Amount */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="stat-task-amount">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-400 uppercase font-mono">Task Amount</span>
              <FileText className="h-4 w-4 text-zinc-500" />
            </div>
            <p className="text-2xl font-bold text-zinc-50" data-testid="task-amount-value">
              {symbol}{stats.taskAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </p>
            <p className="text-xs text-zinc-600 mt-1">{stats.taskEarningsCount} tasks without invoice</p>
          </CardContent>
        </Card>

        {/* To Pay (invoiced, not yet paid) */}
        <Card className="bg-orange-900/20 border-orange-800/30" data-testid="stat-to-pay">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-orange-400 uppercase font-mono">To Pay</span>
              <Receipt className="h-4 w-4 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-orange-400" data-testid="to-pay-value">
              {symbol}{stats.toPayAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </p>
            <p className="text-xs text-zinc-500 mt-1">{realPayments.filter(p => p.status !== "paid").length} invoiced</p>
          </CardContent>
        </Card>

        {/* Paid */}
        <Card className="bg-green-900/20 border-green-800/30" data-testid="stat-paid">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-green-400 uppercase font-mono">Paid</span>
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-400" data-testid="paid-value">
              {symbol}{stats.paidAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </p>
            <p className="text-xs text-zinc-500 mt-1">{realPayments.filter(p => p.status === "paid").length} payments</p>
          </CardContent>
        </Card>

        {/* Active Tasks */}
        <Card className="bg-blue-900/20 border-blue-800/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-blue-400 uppercase font-mono">Active Tasks</span>
              <Briefcase className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-blue-400">{stats.activeTasks}</p>
            <p className="text-xs text-zinc-500 mt-1">{stats.completedTasks} completed</p>
          </CardContent>
        </Card>

        {/* Projects */}
        <Card className="bg-purple-900/20 border-purple-800/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-purple-400 uppercase font-mono">Projects</span>
              <Layers className="h-4 w-4 text-purple-500" />
            </div>
            <p className="text-2xl font-bold text-purple-400">{stats.activeProjects}</p>
            <p className="text-xs text-zinc-500 mt-1">assigned to me</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly Earnings Chart — real payments only */}
        <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-50 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-500" />
              Monthly Earnings - 2026
            </CardTitle>
            <p className="text-xs text-zinc-500">Only invoiced / paid amounts (excludes task amounts without invoices)</p>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#71717a", fontSize: 12 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: "#71717a", fontSize: 12 }} tickFormatter={(v) => `${symbol}${v}`} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: "#18181b", border: "1px solid #3f3f46", borderRadius: "8px" }}
                    labelStyle={{ color: "#fff" }}
                    formatter={(value) => [`${symbol}${value.toLocaleString()}`, ""]}
                  />
                  <Bar dataKey="paid" fill="#22c55e" radius={[4, 4, 0, 0]} name="Paid" />
                  <Bar dataKey="pending" fill="#f59e0b" radius={[4, 4, 0, 0]} name="To Pay" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Payment Status Pie */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-50 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              Payment Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px] flex items-center justify-center">
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-zinc-500">No invoiced payments yet</p>
              )}
            </div>
            <div className="flex justify-center gap-4 mt-2">
              {statusData.map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-xs text-zinc-400">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payments Table — All entries with Type distinction */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-zinc-50 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              My Payments
            </CardTitle>
            <div className="flex items-center gap-3 text-xs">
              <span className="text-zinc-500">
                Task Amount: <span className="text-zinc-50 font-mono">{symbol}{stats.taskAmount.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
              </span>
              <span className="text-zinc-500">
                Invoiced: <span className="text-orange-400 font-mono">{symbol}{stats.toPayAmount.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
              </span>
              <span className="text-zinc-500">
                Paid: <span className="text-green-400 font-mono">{symbol}{stats.paidAmount.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              <DollarSign className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No payments found for your account</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Date</TableHead>
                  <TableHead className="text-zinc-400">Type</TableHead>
                  <TableHead className="text-zinc-400">Task</TableHead>
                  <TableHead className="text-zinc-400">Project</TableHead>
                  <TableHead className="text-zinc-400">Notes</TableHead>
                  <TableHead className="text-zinc-400 text-right">Amount</TableHead>
                  <TableHead className="text-zinc-400 text-center">Payment Deadline</TableHead>
                  <TableHead className="text-zinc-400 text-center">Paid Date</TableHead>
                  <TableHead className="text-zinc-400 text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => {
                  const isOverdue = payment.payment_deadline && payment.status !== "paid" && 
                    new Date(payment.payment_deadline) < new Date();
                  const isTaskEarning = payment.type === "task_earning";
                  return (
                  <TableRow 
                    key={payment.id} 
                    className={cn(
                      "border-zinc-800",
                      isTaskEarning ? "opacity-60 hover:opacity-100 hover:bg-zinc-800/30" : "hover:bg-zinc-800/50"
                    )}
                    data-testid={`payment-row-${payment.id}`}
                  >
                    <TableCell className="text-zinc-400 text-sm">
                      {payment.date?.substring(0, 10) || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge className={cn("text-[10px]", typeBadgeClass(payment.type))} data-testid={`payment-type-${payment.id}`}>
                        {typeLabel(payment.type)}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium text-zinc-50">{payment.task_name || "—"}</TableCell>
                    <TableCell className="text-zinc-400">{payment.project_name || "—"}</TableCell>
                    <TableCell className="text-zinc-400 text-sm max-w-[150px] truncate">
                      {payment.notes || "—"}
                    </TableCell>
                    <TableCell className={cn(
                      "text-right font-mono",
                      isTaskEarning ? "text-zinc-400" : "text-zinc-50"
                    )}>
                      {formatAmount(payment.amount, payment.currency)}
                    </TableCell>
                    <TableCell className={cn(
                      "text-center text-sm",
                      isOverdue ? "text-red-400" : "text-zinc-400"
                    )}>
                      {payment.payment_deadline || "—"}
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      {payment.paid_date ? (
                        <span className="text-green-400 font-mono">{new Date(payment.paid_date).toLocaleDateString()}</span>
                      ) : <span className="text-zinc-600">—</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      {isTaskEarning ? (
                        <Badge className="text-xs bg-zinc-800 text-zinc-500 border-zinc-700">
                          <Clock className="h-3 w-3 mr-1" /> Not invoiced
                        </Badge>
                      ) : (
                        <Badge className={cn(
                          "text-xs",
                          payment.status === "paid" 
                            ? "bg-green-900/30 text-green-400 border-green-700"
                            : payment.status === "approved"
                            ? "bg-blue-900/30 text-blue-400 border-blue-700"
                            : "bg-amber-900/30 text-amber-400 border-amber-700"
                        )}>
                          {payment.status === "paid" ? (
                            <><CheckCircle2 className="h-3 w-3 mr-1" /> Paid</>
                          ) : payment.status === "approved" ? (
                            <><CheckCircle2 className="h-3 w-3 mr-1" /> Approved</>
                          ) : (
                            <><Clock className="h-3 w-3 mr-1" /> Pending</>
                          )}
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Active Tasks */}
      {tasks.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-zinc-50 flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-500" />
              My Tasks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Task</TableHead>
                  <TableHead className="text-zinc-400">Project</TableHead>
                  <TableHead className="text-zinc-400 text-center">Priority</TableHead>
                  <TableHead className="text-zinc-400 text-center">Stage</TableHead>
                  <TableHead className="text-zinc-400 text-right">Deadline</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.slice(0, 10).map((task) => (
                  <TableRow key={task.id} className="border-zinc-800 hover:bg-zinc-800/50">
                    <TableCell className="font-medium text-zinc-50">{task.name || "—"}</TableCell>
                    <TableCell className="text-zinc-400">{task.project_name || "—"}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={cn(
                        "text-xs",
                        task.priority === "high" ? "bg-red-900/30 text-red-400 border-red-700" :
                        task.priority === "medium" ? "bg-amber-900/30 text-amber-400 border-amber-700" :
                        "bg-zinc-800 text-zinc-400 border-zinc-700"
                      )}>
                        {task.priority || "normal"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <TaskStageBadge task={task} size="sm" />
                    </TableCell>
                    <TableCell className="text-right text-zinc-400 text-sm">
                      {task.deadline_phases && task.deadline_phases.length > 0 ? (
                        <DeadlinePhasesBadge phases={task.deadline_phases} />
                      ) : (
                        task.deadline || "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ArtistDashboardPage;
